<script lang="ts">
	import type { ReviewsBlock } from '$models/Block/ReviewsBlock';
	import type { Snippet } from 'svelte';
	import { page } from '$app/state';
	import StarIcon from '$components/UI/Icons/StarIcon.svelte';
	import { getSiteContext } from '$lib/context/site';
	import { formatDate, sanitizeHtml } from '$lib/utils';

	type Review = ReviewsBlock['reviews'][number];

	interface Props {
		block: ReviewsBlock;
		renderTrack: Snippet<[Snippet<[Review, number]>]>;
		showExpandableText?: boolean;
	}

	let { block, renderTrack, showExpandableText = false }: Props = $props();

	const site = getSiteContext();
	const readMoreLabel = page.data.page.layout.translations.buttonLabels.readMoreLabel;
	let expandedReviews = $state<Record<number, boolean>>({});

	function isExpanded(index: number) {
		return !!expandedReviews[index];
	}

	function expandReview(index: number) {
		expandedReviews = {
			...expandedReviews,
			[index]: true
		};
	}
</script>

{#snippet renderReview(review: Review, index: number)}
	<div class="reviews-tile">
		<div class="reviews-tile-content">
			<div class="reviews-tile-header">
				<div class="reviews-tile-stars">
					{#each { length: Number(review.rating) } as starIndex}
						<StarIcon />
					{/each}
				</div>
			</div>
			<div class="reviews-tile-text">
				{#if showExpandableText && review.text?.length > 120 && !isExpanded(index)}
					{@html sanitizeHtml(review.text.slice(0, 120))}...
					<button type="button" class="reviews-tile-toggle" onclick={() => expandReview(index)}>
						{readMoreLabel}
					</button>
				{:else}
					{@html sanitizeHtml(review.text)}
				{/if}
			</div>

			<div class="reviews-tile-footer">
				<div class="reviews-tile-author">
					{review.reviewerName}
				</div>
				<div class="reviews-tile-date">
					{formatDate(review.createdAt, site.locale.code)}
				</div>
			</div>
		</div>
	</div>
{/snippet}

<div class="reviews">
	<div class="reviews-header">
		{#if block.title}
			<h2 class="reviews-header-title">
				{@html sanitizeHtml(block.title)}
			</h2>
		{/if}
		<div class="reviews-header-rating">
			{block.averageRating}
			<div class="reviews-header-stars">
				<StarIcon />
			</div>
			<div class="reviews-header-total">
				{@html sanitizeHtml(block.amountOfReviewsText).replace(
					'%reviews_count%',
					block.amountOfReviews.toString()
				)}
			</div>
			{#if block.text}
				<div class="reviews-header-text">
					{@html sanitizeHtml(block.text)}
				</div>
			{/if}
		</div>
	</div>

	{#if block.reviews.length > 0}
		{@render renderTrack(renderReview)}
	{/if}
</div>

<style lang="postcss" global>
	.reviews {
		display: flex;
		flex-direction: column;
		padding: 40px 24px;
		background-color: var(--color-primary-light);
		border-radius: 16px;

		&-header {
			display: flex;
			flex-direction: column;
			gap: 8px;

			&-rating {
				display: flex;
				align-items: center;
				flex-wrap: wrap;
				gap: 4px;
				font-size: 14px;
				line-height: 1.6;
			}

			&-stars {
				margin-top: -2px;
				svg {
					width: 16px;
					height: 16px;
				}
			}

			&-total {
				&:before,
				&:after {
					content: '|';
					position: relative;
				}

				&:before {
					left: -4px;
					margin-left: 4px;
				}

				&:after {
					display: none;
					right: -4px;
					margin-right: 4px;
				}

				&:not(:last-child) {
					&:after {
						display: inline;
					}
				}
			}

			&-text {
				a {
					color: var(--color-promo);
					text-decoration: underline;
					transition: color 0.3s ease;

					&:hover,
					&:focus {
						color: var(--color-brown);
					}
				}
			}

			&:not(:last-child) {
				margin-bottom: 24px;
			}
		}

		&-tile {
			flex: 1;
			display: flex;
			flex-direction: column;

			&-header {
				display: flex;
				align-items: center;
				margin-bottom: 12px;
			}

			&-stars {
				display: flex;
				align-items: center;

				svg {
					width: 20px;
					height: 20px;
				}
			}

			&-content {
				display: flex;
				flex-direction: column;
				flex: 1;
				position: relative;
				background-color: rgba(255, 255, 255, 0.3);
				padding: 16px;
				border-radius: 8px;
			}

			&-text {
				flex: 1;
				font-size: 16px;
				line-height: 1.6;
				padding-bottom: 16px;
			}

			&-toggle {
				text-decoration: underline;
				transition: color 0.3s ease;

				&:hover,
				&:focus {
					color: var(--color-primary);
				}
			}

			&-footer {
				position: relative;
				display: flex;
				flex-direction: column;
				padding-top: 16px;
				padding-bottom: 6px;

				&::before {
					content: '';
					position: absolute;
					left: 0;
					right: 0;
					top: 0;
					height: 2px;
					background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
					background-size: 5px 2px;
					background-repeat: repeat-x;
					pointer-events: none;
				}
			}

			&-author {
				font-family: var(--title-font-family);
				font-weight: var(--title-font-weight);
				text-transform: var(--title-transform);
				font-size: 14px;
				line-height: 1.2;
			}

			&-date {
				font-size: 12px;
				line-height: 1.6;
			}
		}

		@media (--md) {
			&-header {
				&-rating {
					font-size: 16px;
				}

				&:not(:last-child) {
					margin-bottom: 40px;
				}
			}
		}

		@media (--lg) {
			padding: 0;
			border-radius: 0;
			background-color: transparent;

			&-header-title {
				font-size: 28px;
			}
		}

		@media (--xl) {
			&-tile {
				&-text {
					font-size: 18px;
				}
			}
		}
	}
</style>
